// Still: the visitor's Pause choice, and the start for visitors who ask for reduced motion. The
// companions do nothing of their own (no walking, hopping, falling, flying, parties or moving skins),
// but they stay on the page:
//
// - They sit or stand on the page's own spots (the ones they use while moving, where they fit and
//   cover nothing) and ride along with them as the page scrolls, like a picture in the text.
// - When a spot leaves the screen or loses its room, the companion is gone at once. Once the page
//   has stopped scrolling for a moment it appears at another spot in view, the one nearest to where
//   it was, fading in (no trip there).
// - The bottom of the window only where nothing passes under it (floor.rest), as the last resort.
//   With no free spot at all it waits out of sight, and comes back when one is free.
// - Picked up and let go, it goes straight to the spot nearest to where it was dropped (no fall).
//
// The runtime asks the host where to put companions it would otherwise move (options.place); this
// answers while Still is on, and the host passes its frames and the page's scrolling on to it.
import { tick, animate } from '../engine/core.js';
import { chooseSpot, restless } from '../engine/acts.js';
import { arrive, baseOf } from '../engine/travel.js';
import { FLOOR, visibleOn } from '../engine/world.js';
import { cast, fits, spotPoint } from '../engine/companion.js';

const SETTLE = 350;           // ms without scrolling before companions that lost their spot come back
const RETRY = 1000;           // ms between tries while there's no free spot
const FADE = 120;             // ms of the fade in

export function createStill({ on, ride = false }) {
  let runtime = null;         // set by attach(); newcomers are placed while the runtime is being made
  const away = new Map();     // companions waiting out of sight -> where they were
  let changed = true, quietSince = -Infinity, nextTry = -Infinity;

  // A page spot where it fits, shows and keeps clear of the others (the one nearest to `near`, or one
  // chosen as Moving chooses; never inside a search field); else the floor where nothing passes under
  // it; else null.
  const spotFor = (me, near = null) => chooseSpot(me, { near, filter: l => l !== FLOOR && !l.pill && (near || !l.wallSide) })
    || chooseSpot(me, { near, only: 'floor' });

  // Whether it can stay where it is: still on its spot, which shows it, has room and isn't floor
  // with something under it (or furniture, which goes away when it stops).
  function fine(me) {
    const a = me.anchor;
    if (!a || me.moving || me.held || me.atHome) return false;
    const base = baseOf(a), pt = spotPoint(me, a);
    return !['chair', 'bed', 'caret'].includes(base.kind) && !!pt && fits(me, base) && visibleOn(base, pt.x, pt.y, me.s) && !restless(me);
  }

  // Out of sight until a spot is free (a run of its own that keeps it hidden, so nothing else starts).
  function hide(me) {
    away.set(me, { x: me.x, y: me.y });
    me.anchor = null; me.alpha = 0;
    changed = true;
    if (runtime) keepHidden(me);
  }
  const keepHidden = me => runtime.commands.run(me, async g => { me.anchor = null; me.alpha = 0; for (;;) await tick(g); }, { snap: false });
  // At a spot at once, fading in (its run settles it there; the runtime decides what follows).
  function put(me, spot, fade = true) {
    away.delete(me);
    arrive(me, spot);
    if (!runtime) return;
    if (fade) me.alpha = 0;
    runtime.commands.run(me, async g => {
      if (!fade) return;
      me.alpha = 0;
      await animate(FADE, g, t => { me.alpha = t; });
    });
  }
  // Each of `list` at the free spot nearest to where it is (or was); the others count as standing
  // where they go while each chooses (all of this happens between two frames, so nobody shows early).
  function placeAll(list, { near = me => away.get(me) || { x: me.x, y: me.y }, fallback = null } = {}) {
    const placed = [];
    for (const me of list) {
      const spot = spotFor(me, near(me)) || fallback?.(me);
      if (!spot) { if (!away.has(me)) hide(me); continue; }
      arrive(me, spot); me.alpha = 1;
      placed.push([me, spot]);
    }
    for (const [me, spot] of placed) put(me, spot);
  }

  return {
    // options.place for the runtime: true when Still placed (or hid) the companion itself.
    place(me, why, previous = null) {
      if (!on() && !(ride && why === 'lost' && previous && baseOf(previous) !== FLOOR)) return false;
      if (why === 'new' && !runtime) {                       // while the runtime is being made: no runs yet
        const spot = spotFor(me);
        if (spot) arrive(me, spot); else hide(me);
        return true;
      }
      if (why === 'dropped') {
        const spot = spotFor(me, { x: me.x, y: me.y });
        if (spot) { away.delete(me); arrive(me, spot); } else hide(me);
        return true;
      }
      if (why === 'new') { placeAll([me], { near: () => null }); return true; }
      hide(me);                                              // lost: back once the page is quiet
      return true;
    },
    // The runtime exists: those already waiting get the run that keeps them hidden, the others a
    // run that settles them where they are (so the runtime's opening hop doesn't start).
    attach(rt) {
      runtime = rt;
      if (!on()) return;
      for (const me of cast) {
        if (away.has(me)) keepHidden(me); else rt.commands.run(me, async () => {});
      }
    },
    // Still starts (from Moving or Hidden), after the runtime stopped their own lives: whoever can't
    // stay where it is takes the free spot nearest to it, without a trip. Baby crabs (they hop and
    // fly about on their own) go home at once.
    enter() {
      for (const me of cast) me.babies.length = 0;
      const riding = me => ride && (me.riding || [me.anchor, me.dest].some(a => a && baseOf(a).geom()?.riding));
      placeAll(cast.filter(me => !away.has(me) && !riding(me) && !fine(me)));
      if (ride) for (const me of cast) if (away.has(me)) keepHidden(me);
    },
    // Still ends: whoever is waiting appears at a spot (on the floor if there is none; Moving lets it
    // walk on from there).
    leave() {
      if (ride) {                                           // recovery still waits for a free spot
        for (const me of cast) if (away.has(me)) keepHidden(me);
        return;
      }
      const waiting = [...away.keys()].filter(me => cast.includes(me));
      const floor = me => { const g = FLOOR.geom(); return { ledge: FLOOR, dx: Math.min(Math.max(0, me.x - g.x1), g.x2 - g.x1), rest: 'stand' }; };
      placeAll(waiting, { fallback: floor });
      away.clear();
    },
    scrolled() { changed = true; },
    removed(me) { away.delete(me); },
    // Every frame: once the page has been quiet for a moment, those waiting come back where they can.
    frame(now) {
      if (changed) { quietSince = now; changed = false; }
      if ((!on() && !ride) || !away.size || now - quietSince < SETTLE || now < nextTry) return;
      nextTry = now + RETRY;
      placeAll([...away.keys()].filter(me => cast.includes(me)));
    },
    destroy() { away.clear(); runtime = null; },
  };
}
